import React from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Settings, ChevronRight } from "@mui/icons-material";

const SpecificHandlingXs = ({ id }) => {
  const { t } = useTranslation();

  // Section component for consistent styling
  const Section = ({ title, children }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        fontWeight="600"
        sx={{
          mb: 1.5,
          color: "#333",
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: "100%",
        overflow: "hidden",
        backgroundColor: "#f5f5f7",
      }}
      id={id}
    >
      {/* iOS-style header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          mb: 2,
          pb: 1,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="subtitle1"
          component="h1"
          fontWeight="600"
          sx={{ textAlign: "center" }}
        >
          {t("privacy-policy.specific-handling.title")}
        </Typography>
      </Box>

      {/* Title card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Settings sx={{ color: "#007AFF", fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight="600" sx={{ ml: 1.5 }}>
              {t("privacy-policy.specific-handling.title")}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Usage card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
              {t("privacy-policy.specific-handling.usage.title")}
            </Typography>
          </Box>

          <List disablePadding>
            {t("privacy-policy.specific-handling.usage.purposes", {
              returnObjects: true,
            }).map((purpose, index, array) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <ChevronRight sx={{ color: "#007AFF", fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={purpose}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: { color: "#333" },
                    }}
                  />
                </ListItem>
                {index < array.length - 1 && (
                  <Divider variant="inset" component="li" sx={{ ml: 2 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Scope card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
              {t("privacy-policy.specific-handling.scope.title")}
            </Typography>
          </Box>

          <List disablePadding>
            {t("privacy-policy.specific-handling.scope.items", {
              returnObjects: true,
            }).map((item, index, array) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <ChevronRight sx={{ color: "#007AFF", fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: { color: "#333" },
                    }}
                  />
                </ListItem>
                {index < array.length - 1 && (
                  <Divider variant="inset" component="li" sx={{ ml: 2 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Outsourcing card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Section
            title={t("privacy-policy.specific-handling.outsourcing.title")}
          >
            {/* Content for outsourcing would go here */}
          </Section>
        </CardContent>
      </Card>

      {/* Retention card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Section
            title={t("privacy-policy.specific-handling.retention.title")}
          >
            {/* Content for retention would go here */}
          </Section>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SpecificHandlingXs;
